/* eslint-disable @typescript-eslint/no-floating-promises */
import { toast } from 'react-toastify'
import { getErrorMessage, getFilenameFromDisposition } from '../../helpers'
import { BaseApiResponse, QueryProps } from '../shared/shared.type'
import { apiSlice } from '../api/apiSlice'
import { ErrorType } from '../shared/shared.type'
import {
  CreateAffiliate,
  CreateAmbassador,
  CreateFee,
  GetAffiliateCollectionResponse,
  GetAffiliates,
  GetAffiliatesMetrics,
  GetAmbassadors,
  GetAmbassadorsMetrics,
  GetReferralConfigResponse,
  GetSingleAffiliate,
  GetSingleAmbassador,
  ReferralReportQuery,
  ReferralsAnalyticsResponse,
  ReferralSettingsPayload,
  ReferralSettingsResponse,
  ReferralsReportResponse,
  ReorderCollectionPayload,
  UpdateAmbassador,
  UpsertCollectionPayload,
} from './loyalty.type'

export const loyaltyApiSlice = apiSlice.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    toggleReferral: builder.mutation<BaseApiResponse, ReferralSettingsPayload>({
      query: (payload) => ({
        url: `v1/system-settings/referral-reward`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [{ type: 'REFERRAL', id: 'STATUS' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getReferralStatus: builder.query<ReferralSettingsResponse, void>({
      query: () => ({
        url: `v1/system-settings/referral-reward`,
        method: 'GET',
      }),
      providesTags: [{ type: 'REFERRAL', id: 'STATUS' }],
    }),
    getReferralConfig: builder.query<GetReferralConfigResponse, void>({
      query: () => ({
        url: `v1/fees/referral-reward`,
        method: 'GET',
      }),
      providesTags: [{ type: 'REFERRAL', id: 'CONFIG' }],
    }),
    updateReferralConfig: builder.mutation<BaseApiResponse, CreateFee>({
      query: (payload) => ({
        url: `v1/fees/referral-reward`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [{ type: 'REFERRAL', id: 'CONFIG' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getReferralAnalytics: builder.query<ReferralsAnalyticsResponse, QueryProps>(
      {
        query: (params) => ({
          url: `v2/loyalty/referrals/analytics`,
          method: 'GET',
          params: params,
        }),
        providesTags: ['REFERRAL'],
      },
    ),
    getReferralReport: builder.query<
      ReferralsReportResponse,
      ReferralReportQuery
    >({
      query: (params) => ({
        url: `v2/loyalty/referrals/referrer-report`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['REFERRAL'],
    }),
    exportReferralReports: builder.mutation<
      BaseApiResponse,
      ReferralReportQuery & {
        email: string
      }
    >({
      query: ({ email, ...args }) => ({
        url: `v2/loyalty/referrals/referrer-report/export/email`,
        params: args,
        method: 'POST',
        body: { email },
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    downloadReferralReports: builder.mutation<
      BaseApiResponse,
      ReferralReportQuery
    >({
      query: ({ ...payload }) => {
        return {
          url: 'v2/loyalty/referrals/referrer-report/export/csv',
          method: 'GET',
          params: { ...payload },
          responseHandler: async (response) => {
            if (response.ok) {
              const filename_ = getFilenameFromDisposition(
                response,
                'Referral report.xlsx',
              )

              const blob = await response.blob()

              const url = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = filename_
              document.body.appendChild(a)
              a.click()
              a.remove()
              window.URL.revokeObjectURL(url)

              return undefined
            }

            return response.json()
          },
          responseType: 'blob',
        }
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getAffiliate: builder.query<
      GetSingleAffiliate,
      { id: string; refetch?: boolean }
    >({
      query: ({ id }) => ({
        url: `v2/loyalty/affiliates/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'AFFILIATE', id: arg.id },
      ],
    }),
    getAffiliates: builder.query<GetAffiliates, QueryProps>({
      query: ({ ...params }) => ({
        url: `v2/loyalty/affiliates`,
        method: 'GET',
        params: params,
      }),
      providesTags: [{ type: 'AFFILIATE', id: 'LIST' }],
    }),
    getAffiliatesMetrics: builder.query<GetAffiliatesMetrics, QueryProps>({
      query: ({ ...params }) => ({
        url: `v2/loyalty/affiliates/analytics`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['AFFILIATE'],
    }),
    createAffiliate: builder.mutation<BaseApiResponse, CreateAffiliate>({
      query: (payload) => ({
        url: `v2/loyalty/affiliates`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'AFFILIATE', id: 'LIST' }, 'AFFILIATE'],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    updateAffiliate: builder.mutation<
      BaseApiResponse,
      { id: string; payload: Partial<CreateAffiliate> }
    >({
      query: ({ id, payload }) => ({
        url: `v2/loyalty/affiliates/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AFFILIATE', id: arg.id },
        { type: 'AFFILIATE', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    deleteAffiliateList: builder.mutation<
      BaseApiResponse,
      { affiliateId: string; listId: string }
    >({
      query: ({ affiliateId, listId }) => ({
        url: `v2/loyalty/affiliates/${affiliateId}/lists/${listId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AFFILIATE_COLLECTION', id: arg.affiliateId },
        { type: 'AFFILIATE', id: arg.affiliateId },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    deleteAffiliate: builder.mutation<BaseApiResponse, { id: string }>({
      query: ({ id }) => ({
        url: `v2/loyalty/affiliates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AFFILIATE', id: arg.id },
        { type: 'AFFILIATE', id: 'LIST' },
        'AFFILIATE',
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getAffiliateCollection: builder.query<
      GetAffiliateCollectionResponse,
      { affiliateId: string; refetch?: boolean }
    >({
      query: ({ affiliateId }) => ({
        url: `v2/loyalty/affiliates/${affiliateId}/collection`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'AFFILIATE_COLLECTION', id: arg.affiliateId },
      ],
    }),
    upsertAffiliateCollection: builder.mutation<
      GetAffiliateCollectionResponse,
      { affiliateId: string; payload: UpsertCollectionPayload }
    >({
      query: ({ affiliateId, payload }) => ({
        url: `v2/loyalty/affiliates/${affiliateId}/collection`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AFFILIATE_COLLECTION', id: arg.affiliateId },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    reorderAffiliateCollection: builder.mutation<
      GetAffiliateCollectionResponse,
      { affiliateId: string; payload: ReorderCollectionPayload }
    >({
      query: ({ affiliateId, payload }) => ({
        url: `v2/loyalty/affiliates/${affiliateId}/collection/reorder`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AFFILIATE_COLLECTION', id: arg.affiliateId },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getAmbassador: builder.query<
      GetSingleAmbassador,
      { id: string; refetch?: boolean }
    >({
      query: ({ id }) => ({
        url: `v2/loyalty/ambassadors/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'AMBASSADOR', id: arg.id },
      ],
    }),
    getAmbassadors: builder.query<GetAmbassadors, QueryProps>({
      query: ({ ...params }) => ({
        url: `v2/loyalty/ambassadors`,
        method: 'GET',
        params: params,
      }),
      providesTags: [{ type: 'AMBASSADOR', id: 'LIST' }],
    }),
    getAmbassadorsMetrics: builder.query<GetAmbassadorsMetrics, QueryProps>({
      query: ({ ...params }) => ({
        url: `v2/loyalty/ambassadors/analytics`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['AMBASSADOR'],
    }),
    createAmbassador: builder.mutation<BaseApiResponse, CreateAmbassador>({
      query: (payload) => ({
        url: `v2/loyalty/ambassadors`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'AMBASSADOR', id: 'LIST' }, 'AMBASSADOR'],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    updateAmbassador: builder.mutation<
      BaseApiResponse,
      { id: string; payload: UpdateAmbassador }
    >({
      query: ({ id, payload }) => ({
        url: `v2/loyalty/ambassadors/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AMBASSADOR', id: arg.id },
        { type: 'AMBASSADOR', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    deleteAmbassador: builder.mutation<BaseApiResponse, { id: string }>({
      query: ({ id }) => ({
        url: `v2/loyalty/ambassadors/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AMBASSADOR', id: arg.id },
        { type: 'AMBASSADOR', id: 'LIST' },
        'AMBASSADOR',
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
  }),
})

export const {
  useGetReferralStatusQuery,
  useToggleReferralMutation,
  useUpdateReferralConfigMutation,
  useGetReferralConfigQuery,
  useGetReferralAnalyticsQuery,
  useGetReferralReportQuery,
  useExportReferralReportsMutation,
  useDownloadReferralReportsMutation,
  useGetAffiliateQuery,
  useGetAffiliatesQuery,
  useGetAffiliatesMetricsQuery,
  useCreateAffiliateMutation,
  useUpdateAffiliateMutation,
  useDeleteAffiliateMutation,
  useDeleteAffiliateListMutation,
  useGetAffiliateCollectionQuery,
  useLazyGetAffiliateCollectionQuery,
  useUpsertAffiliateCollectionMutation,
  useReorderAffiliateCollectionMutation,
  useGetAmbassadorQuery,
  useGetAmbassadorsQuery,
  useGetAmbassadorsMetricsQuery,
  useCreateAmbassadorMutation,
  useUpdateAmbassadorMutation,
  useDeleteAmbassadorMutation,
} = loyaltyApiSlice
